import { Form } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { UploadFile } from 'components/ui/UploadFile/UploadFile'
import { Alert, Button, Divider, Flex, Input, Title } from 'components/uiKit'
import { ConvertedFile } from 'utils/helpers/files/fileUpload'
import { getActivateOrderRoute } from 'utils/helpers/routes/getRoutes'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { PHOTO_VALIDATE } from 'shared/constants/values'

import { eLockPhotoTypeToTitle } from '../../data'
import { ElockInfoStatus, ELockPhoto } from '../../types'
import { OpenELockModal } from '../OpenELockModal/OpenELockModal'

type ActivateELockFormProps = {
  fromOrder?: boolean
}

export const ActivateELockForm = observer(({ fromOrder = false }: ActivateELockFormProps) => {
  const { eLockUiStore: uiStore } = useStore()

  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const isActive = uiStore.isActive
  const isRemark = uiStore.eLockReason.status === ElockInfoStatus.remark
  const isDisable = (uiStore.eLockName.trim().length === 0 || uiStore.invalidPhotos) && !isActive
  const isFormDisabled = (!isRemark && isActive) || fromOrder

  useEffect(() => {
    form.setFieldValue('lockName', uiStore.eLockName)
  }, [uiStore.eLockName])

  const changeFileHandler = (file: ConvertedFile, fileType: ELockPhoto) => {
    uiStore.setPhotoData(fileType)({ url: file.content, size: file.fileSize })
  }

  const deleteButtonHandler = (fileType: ELockPhoto) => {
    uiStore.setPhotoData(fileType)({ url: '', size: 0 })
  }

  const getELockConnectInfo = async () => {
    setLoading(true)

    const result = await uiStore.getELockConnectInfo()

    if (result) {
      setIsModalVisible(true)
    }

    setLoading(false)
  }

  const updateELock = async () => {
    setLoading(true)
    const result = await uiStore.updateELock()
    setLoading(false)

    if (result && uiStore.orderId) {
      navigate(getActivateOrderRoute(uiStore.orderId))
    }
  }

  const getKey = (key: string) => `${OrderKeys.eLockModule}.${key}`

  return (
    <Flex vertical gap={16} style={{ maxWidth: 490 }}>
      {fromOrder && <Title level={5}>{t(getKey('Активация электронного замка'))}</Title>}

      {!fromOrder && isActive && <Alert message={t(getKey('Вы активировали замок'))} type="info" />}

      <Form layout="vertical" form={form} requiredMark={false}>
        <Form.Item<{ lockName: string }>
          label={t(getKey('Код контроллера'))}
          name={'lockName'}
          rules={[{ required: true, message: t(getSharedKey('required')) }]}
          tooltip={t(getKey('Можно найти на корпусе'))}
        >
          <Input
            placeholder="WBxxxA"
            value={uiStore.eLockName}
            onChange={(e) => {
              form.setFieldValue('lockName', e.target.value.toUpperCase().trim())
              uiStore.setELockName(e.target.value.toUpperCase().trim())
            }}
            maxLength={500}
            disabled={isActive}
          />
        </Form.Item>

        <Form.Item>
          <UploadFile
            contentUrl={uiStore.doorFromInsidePhoto.url}
            fileValidationOptions={PHOTO_VALIDATE}
            label={t(getKey(eLockPhotoTypeToTitle[ELockPhoto.doorFromInside]))}
            description={t(getKey('Описание двери изнутри'))}
            onChange={(file) => changeFileHandler(file, ELockPhoto.doorFromInside)}
            onDelete={() => deleteButtonHandler(ELockPhoto.doorFromInside)}
            disabled={isFormDisabled}
          />
        </Form.Item>

        <Divider />

        <Form.Item>
          <UploadFile
            contentUrl={uiStore.doorFromTheEndPhoto.url}
            fileValidationOptions={PHOTO_VALIDATE}
            label={t(getKey(eLockPhotoTypeToTitle[ELockPhoto.doorFromTheEnd]))}
            description={t(getKey('Описание двери с торца'))}
            onChange={(file) => changeFileHandler(file, ELockPhoto.doorFromTheEnd)}
            onDelete={() => deleteButtonHandler(ELockPhoto.doorFromTheEnd)}
            disabled={isFormDisabled}
          />
        </Form.Item>

        <Divider />

        <Form.Item>
          <UploadFile
            contentUrl={uiStore.controllerPhoto.url}
            fileValidationOptions={PHOTO_VALIDATE}
            label={t(getKey(eLockPhotoTypeToTitle[ELockPhoto.controller]))}
            description={t(getKey('Описание контроллера'))}
            onChange={(file) => changeFileHandler(file, ELockPhoto.controller)}
            onDelete={() => deleteButtonHandler(ELockPhoto.controller)}
            disabled={isFormDisabled}
          />
        </Form.Item>

        <Divider />

        {!fromOrder &&
          (isRemark ? (
            <Button onClick={updateELock} loading={loading} disabled={uiStore.invalidPhotos}>
              {t(getKey('Обновить фотографии замка'))}
            </Button>
          ) : (
            <Button onClick={getELockConnectInfo} disabled={isDisable} loading={loading} htmlType="submit">
              {t(getKey('Получить данные для открытия'))}
            </Button>
          ))}
      </Form>

      {uiStore.orderId && (
        <OpenELockModal
          orderId={uiStore.orderId}
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
        />
      )}
    </Flex>
  )
})
